import { APayoutAWalletCanRequest } from '../../domains/the-payout-a-wallet-can-request';
import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  CreatePayoutRequestResponse,
  GetPayoutDetailsResponse,
  GetPayoutHistoryResponse,
  OrganizersService,
  PaymentsService,
  PayoutRequestDto,
} from '@smatchy/sdk';
import {
  CreatePayout,
  CreatePayoutSuccess,
  FailAction,
  GetPayoutAvailable,
  GetPayoutAvailableSuccess,
  GetPayoutDetails,
  GetPayoutDetailsSuccess,
  GetPayouts,
  GetPayoutsSuccess,
} from './payout.actions';
import { catchError, of, tap, throwError } from 'rxjs';
export interface PayoutStateModel {
  /** null until the history has been loaded, which is what the defaults set. */
  payouts: GetPayoutHistoryResponse | null;
  payoutAvailable: APayoutAWalletCanRequest[];
  loading: boolean;
  error?: unknown;
  /** null until a payout has been requested, which is what the defaults set. */
  requestPayout: CreatePayoutRequestResponse | null;
  /**
   * Two different answers land here, which `as any` used to hide.
   *
   * Requesting a payout answers with CreatePayoutRequestResponse: id,
   * organizerId, amount, status, requestedAt and events as an array of ids.
   * Fetching the details answers with GetPayoutDetailsResponse, which also
   * carries the organizer, the bank account, and events as objects.
   *
   * So right after a request is created, this field holds the smaller shape.
   * The union says so rather than pretending otherwise; a reader now has to
   * ask which one it has.
   */
  payoutDetails?: GetPayoutDetailsResponse | CreatePayoutRequestResponse;
}
const initialState: PayoutStateModel = {
  payouts: null,
  payoutAvailable: [],
  loading: false,
  error: null,
  requestPayout: null,
};
@State({
  name: 'payouts',
  defaults: initialState,
})
@Injectable()
export class PayoutState {
  private readonly _organizersService = inject(OrganizersService);
  private readonly _paymentService = inject(PaymentsService);
  @Action(GetPayouts)
  getPayouts({ patchState, dispatch }: StateContext<PayoutStateModel>, { payload }: GetPayouts) {
    patchState({ loading: true, error: null });
    return this._organizersService.getPayoutHistory(payload).pipe(
      tap((response) => {
        dispatch(new GetPayoutsSuccess(response as GetPayoutHistoryResponse));
      }),
      catchError((error) => {
        console.error('Failed to load payouts:', error);
        dispatch(new FailAction(error));
        return of(error);
      }),
    );
  }
  @Action(GetPayoutsSuccess)
  getPayoutsSuccess({ patchState }: StateContext<PayoutStateModel>, { payload }: GetPayoutsSuccess) {
    return patchState({
      payouts: payload,
      loading: false,
    });
  }
  @Action(GetPayoutDetails)
  getPayoutDetails(ctx: StateContext<PayoutStateModel>, { query: { id } }: GetPayoutDetails) {
    ctx.patchState({ loading: true, error: null });
    return this._organizersService
      .getPayoutOrganizerDetails({
        id,
      })
      .pipe(
        tap((response) => {
          ctx.dispatch(new GetPayoutDetailsSuccess(response as GetPayoutDetailsResponse));
        }),
        catchError((error) => {
          ctx.dispatch(new FailAction(error));
          return of(error);
        }),
      );
  }
  @Action(GetPayoutDetailsSuccess)
  getPayoutDetailsSuccess(ctx: StateContext<PayoutStateModel>, { payload }: GetPayoutDetailsSuccess) {
    return ctx.patchState({
      loading: false,
      payoutDetails: payload,
    });
  }
  @Action(CreatePayout)
  createPayout(ctx: StateContext<PayoutStateModel>) {
    ctx.patchState({ loading: true, error: null });
    return this._organizersService.createPayoutRequest().pipe(
      tap((response) => {
        ctx.dispatch(new CreatePayoutSuccess(response as CreatePayoutRequestResponse));
      }),
      catchError((error) => {
        ctx.dispatch(new FailAction(error));
        return throwError(() => error);
      }),
    );
  }
  @Action(CreatePayoutSuccess)
  createPayoutSuccess(ctx: StateContext<PayoutStateModel>, { payload }: CreatePayoutSuccess) {
    return ctx.patchState({
      loading: false,
      requestPayout: payload,
      payoutDetails: payload,
    });
  }
  @Action(GetPayoutAvailableSuccess)
  getPayoutAvailableSuccess(ctx: StateContext<PayoutStateModel>, { payload }: GetPayoutAvailableSuccess) {
    return ctx.patchState({
      loading: false,
      payoutAvailable: payload,
    });
  }

  @Action(GetPayoutAvailable)
  getPayoutAvailable(ctx: StateContext<PayoutStateModel>) {
    ctx.patchState({ loading: true, error: null });
    return this._paymentService.findPaidEventAvailableForPayout().pipe(
      tap((response) => {
        // The route carries no @SwaggerApiResponse, so the generated client
        // types this as an envelope it does not use. Asserted once, to the
        // shape the aggregation actually builds; see
        // domains/the-payout-a-wallet-can-request.ts. This was `as any`, which
        // hid that the declared type had no field in common with the payload.
        ctx.dispatch(new GetPayoutAvailableSuccess(response as unknown as APayoutAWalletCanRequest[]));
      }),
      catchError((error) => {
        ctx.dispatch(new FailAction(error));
        return of(error);
      }),
    );
  }
  @Action(FailAction)
  loadFail(ctx: StateContext<PayoutStateModel>, action: FailAction) {
    return ctx.patchState({
      loading: false,
      error: action.error,
    });
  }
}
